/**
 * Hash Router Utilities
 * Lightweight hash-based routing for the Claude Web application
 */

// Route type definitions
export type RouteType =
  | { type: 'chat'; conversationId?: string }
  | { type: 'chats' }
  | { type: 'projects' }
  | { type: 'project'; projectId: string }
  | { type: 'project-chat'; projectId: string; conversationId: string }
  | { type: 'project-pdfs'; projectId: string }
  | { type: 'project-members'; projectId: string } // 🔥 新增：项目成员管理
  | { type: 'agents' }
  | { type: 'shared' }
  | { type: 'user-settings' }; // 🔥 新增：用户设置页面

// Route patterns for matching
const ROUTE_PATTERNS = {
  chat: /^#\/chat$/,
  chatWithId: /^#\/chat\/([^/]+)$/,
  chats: /^#\/chats$/,
  projects: /^#\/projects$/,
  project: /^#\/project\/([^/]+)$/,
  projectChat: /^#\/project\/([^/]+)\/chat\/([^/]+)$/,
  projectPdfs: /^#\/project\/([^/]+)\/pdfs$/,
  projectMembers: /^#\/project\/([^/]+)\/members$/, // 🔥 新增：项目成员管理
  agents: /^#\/agents$/,
  shared: /^#\/shared$/,
  userSettings: /^#\/user-settings$/, // 🔥 新增：用户设置页面
};

/**
 * Parse a hash string into a RouteType object
 * @param hash - The hash string (e.g., "#/chat/123")
 * @returns RouteType object or null if no match
 */
export function parseHash(hash: string): RouteType | null {
  if (!hash || hash === '#' || hash === '#/') {
    return { type: 'chat' };
  }

  // Project chat (must check before project)
  const projectChatMatch = hash.match(ROUTE_PATTERNS.projectChat);
  if (projectChatMatch) {
    return {
      type: 'project-chat',
      projectId: decodeURIComponent(projectChatMatch[1]),
      conversationId: decodeURIComponent(projectChatMatch[2]),
    };
  }

  // Project PDFs (must check before project)
  const projectPdfsMatch = hash.match(ROUTE_PATTERNS.projectPdfs);
  if (projectPdfsMatch) {
    return {
      type: 'project-pdfs',
      projectId: decodeURIComponent(projectPdfsMatch[1]),
    };
  }

  // Project members (must check before project)
  const projectMembersMatch = hash.match(ROUTE_PATTERNS.projectMembers);
  if (projectMembersMatch) {
    return {
      type: 'project-members',
      projectId: decodeURIComponent(projectMembersMatch[1]),
    };
  }

  // Project detail
  const projectMatch = hash.match(ROUTE_PATTERNS.project);
  if (projectMatch) {
    return {
      type: 'project',
      projectId: decodeURIComponent(projectMatch[1]),
    };
  }

  // Chat with conversation ID
  const chatWithIdMatch = hash.match(ROUTE_PATTERNS.chatWithId);
  if (chatWithIdMatch) {
    return {
      type: 'chat',
      conversationId: decodeURIComponent(chatWithIdMatch[1]),
    };
  }

  // Simple routes
  if (ROUTE_PATTERNS.chat.test(hash)) {
    return { type: 'chat' };
  }
  if (ROUTE_PATTERNS.chats.test(hash)) {
    return { type: 'chats' };
  }
  if (ROUTE_PATTERNS.projects.test(hash)) {
    return { type: 'projects' };
  }
  if (ROUTE_PATTERNS.agents.test(hash)) {
    return { type: 'agents' };
  }
  if (ROUTE_PATTERNS.shared.test(hash)) {
    return { type: 'shared' };
  }
  if (ROUTE_PATTERNS.userSettings.test(hash)) {
    return { type: 'user-settings' };
  }

  // Default to chat for unrecognized routes
  return null;
}

/**
 * Generate a hash string from a RouteType object
 * @param route - The RouteType object
 * @returns Hash string (e.g., "#/chat/123")
 */
export function generateHash(route: RouteType): string {
  switch (route.type) {
    case 'chat':
      return route.conversationId
        ? `#/chat/${encodeURIComponent(route.conversationId)}`
        : '#/chat';
    case 'chats':
      return '#/chats';
    case 'projects':
      return '#/projects';
    case 'project':
      return `#/project/${encodeURIComponent(route.projectId)}`;
    case 'project-chat':
      return `#/project/${encodeURIComponent(route.projectId)}/chat/${encodeURIComponent(route.conversationId)}`;
    case 'project-pdfs':
      return `#/project/${encodeURIComponent(route.projectId)}/pdfs`;
    case 'project-members':
      return `#/project/${encodeURIComponent(route.projectId)}/members`;
    case 'agents':
      return '#/agents';
    case 'shared':
      return '#/shared';
    case 'user-settings':
      return '#/user-settings';
    default:
      return '#/chat';
  }
}

/**
 * Navigate to a route by updating window.location.hash
 * @param route - The RouteType object to navigate to
 */
export function navigateTo(route: RouteType): void {
  const hash = generateHash(route);
  if (window.location.hash !== hash) {
    window.location.hash = hash;
  }
}

/**
 * Get the current route from window.location.hash
 * @returns RouteType object or default chat route
 */
export function getCurrentRoute(): RouteType {
  return parseHash(window.location.hash) || { type: 'chat' };
}

/**
 * Check if OAuth redirect is in progress (has ?code= parameter)
 * @returns true if OAuth redirect is detected
 */
export function isOAuthRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('code');
}

/**
 * Force navigate to a route, even if the hash is already the target value
 * This ensures the route is applied even when the URL hash matches
 * Useful after login to sync UI state with URL
 * @param route - The RouteType object to navigate to
 */
export function forceNavigateTo(route: RouteType): void {
  const hash = generateHash(route);
  if (window.location.hash === hash) {
    // Hash already matches, manually dispatch hashchange event to re-apply route
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    window.location.hash = hash;
  }
}
